/**
 * MessagingContext - Centralized state management for app-wide messaging
 *
 * Provides context for managing toast notifications and bell/alert notifications
 * with persistent storage for bell messages and real-time state updates.
 *
 * The bell cache is scoped to the active tenant and written from a debounced
 * effect, never from inside a `setMessages` updater.
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'

import {
  BELL_PERSIST_BYTE_BUDGET,
  BELL_PERSIST_DEBOUNCE_MS,
  BELL_STORAGE_KEY_PREFIX,
  BELL_STORAGE_OVER_BUDGET_WARNING,
  type Message,
  type MessagingContextValue,
} from '../types/messaging'

import { TenantContext } from './TenantContext'

const MessagingContext = createContext<MessagingContextValue | undefined>(
  undefined,
)

const textEncoder = new TextEncoder()

const serializedByteLength = (payload: string): number =>
  textEncoder.encode(payload).length

const timestampOf = (message: Message): number =>
  message.timestamp ? message.timestamp.getTime() : 0

/**
 * Shrink the bell cache into `BELL_PERSIST_BYTE_BUDGET`, measured on the
 * serialized payload rather than a message count.
 *
 * Only **read** rows are evicted, oldest first. Unread rows are never dropped:
 * deleting a notification the user has not seen, with no user action, would
 * lose the only record of it (FR-6.1.11). When only unread rows remain and the
 * payload still does not fit, `overBudget` tells the caller to abandon the
 * write rather than delete anything.
 */
const capBellCacheToBudget = (
  bellMessages: Message[],
): { payload: string; overBudget: boolean } => {
  let payload = JSON.stringify(bellMessages)
  if (serializedByteLength(payload) <= BELL_PERSIST_BYTE_BUDGET) {
    return { payload, overBudget: false }
  }

  const evictionOrder = bellMessages
    .map((message, index) => ({ message, index }))
    .filter(({ message }) => message.read === true)
    .sort((a, b) => {
      const aTime = timestampOf(a.message)
      const bTime = timestampOf(b.message)
      return aTime === bTime ? a.index - b.index : aTime - bTime
    })

  const evicted = new Set<number>()
  for (const { index } of evictionOrder) {
    evicted.add(index)
    payload = JSON.stringify(bellMessages.filter((_msg, i) => !evicted.has(i)))
    if (serializedByteLength(payload) <= BELL_PERSIST_BYTE_BUDGET) {
      return { payload, overBudget: false }
    }
  }

  return { payload, overBudget: true }
}

/** Parses a persisted bell payload into `Message[]` (restoring `timestamp`).
 *  Shared by the mount-read effect and the `storage` listener. */
const parsePersistedBell = (stored: string | null): Message[] => {
  if (!stored) return []
  try {
    return (JSON.parse(stored) as Message[]).map(msg => ({
      ...msg,
      timestamp: msg.timestamp ? new Date(msg.timestamp) : new Date(),
    }))
  } catch (error) {
    console.warn('Failed to load persisted bell messages:', error)
    return []
  }
}

/** Order-independent signature over the fields that decide cross-tab
 *  convergence (id/sourceId/read/target). The `storage` listener compares this
 *  to skip a no-op `setMessages` — without it, peer writes ping-pong forever. */
const bellSignature = (messages: Message[]): string =>
  JSON.stringify(
    messages
      .filter(msg => msg.target === 'bell' || msg.target === 'both')
      .map(msg => ({
        id: msg.id ?? null,
        sourceId: msg.sourceId ?? null,
        read: msg.read === true,
        target: msg.target,
      }))
      .sort((a, b) => {
        const aKey = `${a.sourceId ?? ''}|${a.id ?? ''}`
        const bKey = `${b.sourceId ?? ''}|${b.id ?? ''}`
        return aKey < bKey ? -1 : aKey > bKey ? 1 : 0
      }),
  )

/** `setMessages` updater that swaps in a fresh bell set while preserving
 *  in-flight toasts (demoting `both` → `toast`). Shared by the mount-read
 *  effect and the cross-tab `storage` listener. */
const replaceBellUpdater =
  (restored: Message[]) =>
  (prev: Message[]): Message[] => [
    ...prev
      .filter(msg => msg.target === 'toast' || msg.target === 'both')
      .map(msg =>
        msg.target === 'both' ? { ...msg, target: 'toast' as const } : msg,
      ),
    ...restored,
  ]

interface MessagingProviderProps {
  children: React.ReactNode
}

export const MessagingProvider = ({
  children,
}: MessagingProviderProps): React.JSX.Element => {
  const [messages, setMessages] = useState<Message[]>([])
  const [persistenceWarning, setPersistenceWarning] = useState<string | null>(
    null,
  )

  // `useContext(TenantContext)`, not `useTenant()`: the latter throws without a
  // provider, breaking every suite that mounts `MessagingProvider` alone.
  const tenantContext = useContext(TenantContext)
  const tenantId = tenantContext?.selectedTenant?.id
  // `null` until a tenant is selected, so neither the read nor the write path
  // can touch a `messaging-bell-notifications:undefined` bucket.
  const storageKey = tenantId ? `${BELL_STORAGE_KEY_PREFIX}:${tenantId}` : null

  // Write the tenant's bell cache. Called only from the debounced effect below.
  const persistBellMessages = useCallback(
    (key: string, allMessages: Message[]): void => {
      const bellMessages = allMessages.filter(
        msg => msg.target === 'bell' || msg.target === 'both',
      )
      const { payload, overBudget } = capBellCacheToBudget(bellMessages)

      if (overBudget) {
        console.warn(
          'Bell notifications exceed the local cache budget; skipping the write. Unread notifications are never evicted to make room.',
        )
        setPersistenceWarning(BELL_STORAGE_OVER_BUDGET_WARNING)
        return
      }

      try {
        localStorage.setItem(key, payload)
        setPersistenceWarning(null)
      } catch (error) {
        console.warn('Failed to persist bell messages:', error)
        setPersistenceWarning(BELL_STORAGE_OVER_BUDGET_WARNING)
      }
    },
    [],
  )

  // Load the active tenant's persisted bell messages. Keyed on `storageKey` so a
  // tenant switch re-reads — an empty dep array would leave tenant A's messages
  // in tenant B's bell.
  useEffect(() => {
    if (!storageKey) return

    const restored = parsePersistedBell(localStorage.getItem(storageKey))

    // Replace the bell half UNCONDITIONALLY, even when the new tenant has
    // nothing cached — bailing out on an empty read leaves the previous tenant's
    // notifications on screen. In-flight toasts survive the switch; a 'both'
    // message is demoted to 'toast' so its bell half does not cross the tenant
    // boundary.
    setMessages(replaceBellUpdater(restored))
  }, [storageKey])

  // Latest `messages`, for the `storage` listener's loop guard — a ref (not a
  // dependency) so the listener's deps stay `[storageKey]` and we subscribe
  // once per tenant, not on every message change.
  const messagesRef = useRef(messages)
  messagesRef.current = messages

  /** Server ids dismissed locally but not yet confirmed dropped by the server
   *  (B11) — a ref, not state, so noting a dismissal doesn't trigger a render.
   *  Pruned by `reconcileDismissedSourceIds` once the server confirms the drop. */
  const dismissedSourceIdsRef = useRef<Set<number>>(new Set())

  const noteDismissedSourceIds = useCallback(
    (sourceIds: readonly number[]): void => {
      for (const sourceId of sourceIds) {
        dismissedSourceIdsRef.current.add(sourceId)
      }
    },
    [],
  )

  // Sticky suppression, pruned by server confirmation: drop any suppressed id
  // the latest poll no longer returns (dismiss landed), keep the rest (still
  // in flight/failed). An empty `presentIds` correctly prunes everything.
  const reconcileDismissedSourceIds = useCallback(
    (presentIds: ReadonlySet<number>): ReadonlySet<number> => {
      const current = dismissedSourceIdsRef.current
      for (const sourceId of current) {
        if (!presentIds.has(sourceId)) {
          current.delete(sourceId)
        }
      }
      return current
    },
    [],
  )

  // B7 — cross-tab durability: a dismissal in one tab was clobbered by a peer's
  // later debounced write of its (still-stale) bell set. Mirror the mount-read
  // effect on a peer's `storage` event so all tabs converge on the last writer.
  useEffect(() => {
    if (!storageKey) return

    const handleStorage = (event: StorageEvent): void => {
      // Only our tenant's key. Ignores other tenants' keys and a whole-store
      // `clear()`, which fires with `key === null`.
      if (event.key !== storageKey) return

      const restored = parsePersistedBell(event.newValue)

      // Honor this tab's sticky dismiss suppression before replacing — a peer's
      // set can still carry a row THIS tab just dismissed (POST not round-tripped
      // yet). Filter it out here; do NOT prune the ref (pruning stays payload-driven in the producer's reconcile).
      const suppressed = dismissedSourceIdsRef.current
      const filtered =
        suppressed.size === 0
          ? restored
          : restored.filter(
              msg =>
                msg.sourceId === undefined || !suppressed.has(msg.sourceId),
            )

      // Loop guard: replace only when the peer's (filtered) bell set differs from
      // ours. `storage` never fires in the writing tab, so this guards the
      // receiver's follow-up write — skip a no-op write and cross-tab writes stop instead of ping-ponging.
      if (bellSignature(filtered) === bellSignature(messagesRef.current)) return

      setMessages(replaceBellUpdater(filtered))
    }

    window.addEventListener('storage', handleStorage)
    return () => window.removeEventListener('storage', handleStorage)
  }, [storageKey])

  // Debounced persistence. An effect rather than the `setMessages` updaters:
  // that keeps the synchronous `localStorage.setItem` out of the render phase
  // (StrictMode double-invokes it) and collapses bursts into one write.
  useEffect(() => {
    if (!storageKey) return

    const timer = setTimeout(() => {
      persistBellMessages(storageKey, messages)
    }, BELL_PERSIST_DEBOUNCE_MS)

    return () => clearTimeout(timer)
  }, [messages, storageKey, persistBellMessages])

  // Generate unique ID for messages
  const generateId = useCallback((): string => {
    return `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
  }, [])

  // Add a new message; caller-supplied `timestamp` / `read` are honoured.
  const addMessage = useCallback(
    (messageData: Omit<Message, 'id'>): string => {
      const id = generateId()

      const newMessage: Message = {
        ...messageData,
        id,
        timestamp: messageData.timestamp ?? new Date(),
        read: messageData.read ?? false,
      }

      setMessages(prev => [...prev, newMessage])

      return id
    },
    [generateId],
  )

  // Patch a live message by id. Used to coalesce a batch toast (update its
  // count + Undo command in place) and to swap a success toast to an error
  // state without stacking a second toast. Omitting `timestamp` installs a
  // fresh one, restarting the auto-dismiss window (both the ToastItem timer and
  // the sweeper key off it); a supplied one is kept verbatim so a bell patch
  // preserves the backend `created_at`. No-op when the id is absent.
  const updateMessage = useCallback(
    (id: string, patch: Partial<Omit<Message, 'id'>>): void => {
      setMessages(prev => {
        let found = false
        const updated = prev.map(msg => {
          if (msg.id === id) {
            found = true
            return {
              ...msg,
              ...patch,
              id: msg.id,
              timestamp: patch.timestamp ?? new Date(),
            }
          }
          return msg
        })
        if (!found) return prev
        return updated
      })
    },
    [],
  )

  // Mark a bell message as read. Same `!found` shape as updateMessage: a no-op
  // call must not rebuild the array, which would re-render every consumer.
  const markAsRead = useCallback((messageId: string): void => {
    setMessages(prev => {
      let found = false
      const updated = prev.map(msg => {
        if (msg.id === messageId && msg.read !== true) {
          found = true
          return { ...msg, read: true }
        }
        return msg
      })
      if (!found) return prev
      return updated
    })
  }, [])

  // Mark all bell messages as read
  const markAllAsRead = useCallback((): void => {
    setMessages(prev =>
      prev.map(msg =>
        msg.target === 'bell' || msg.target === 'both'
          ? { ...msg, read: true }
          : msg,
      ),
    )
  }, [])

  // Remove a specific message
  const removeMessage = useCallback((messageId: string): void => {
    setMessages(prev => prev.filter(msg => msg.id !== messageId))
  }, [])

  // Clear all messages
  const clearAllMessages = useCallback((): void => {
    setMessages([])
  }, [])

  // Clear only bell messages
  const clearBellMessages = useCallback((): void => {
    setMessages(prev => prev.filter(msg => msg.target === 'toast'))
  }, [])

  // Clear only toast messages
  const clearToastMessages = useCallback((): void => {
    setMessages(prev => prev.filter(msg => msg.target !== 'toast'))
  }, [])

  // Remove a toast message (converts 'both' to 'bell', removes 'toast' entirely)
  const removeToastMessage = useCallback((messageId: string): void => {
    setMessages(prev =>
      prev
        .map(msg => {
          if (msg.id === messageId) {
            if (msg.target === 'both') {
              // Convert 'both' to 'bell' only
              return { ...msg, target: 'bell' as const }
            }
            // For 'toast' only messages, mark for removal
            return null
          }
          return msg
        })
        .filter((msg): msg is Message => msg !== null),
    )
  }, [])

  // Derived state - compute filtered message arrays
  const toastMessages = messages.filter(
    msg => msg.target === 'toast' || msg.target === 'both',
  )

  const bellMessages = messages.filter(
    msg => msg.target === 'bell' || msg.target === 'both',
  )

  const unreadCount = bellMessages.filter(msg => !msg.read).length

  // Auto-cleanup toast messages after they expire (for non-persistent toasts)
  useEffect(() => {
    const interval = setInterval(() => {
      const now = Date.now()
      const expiredToasts = toastMessages.filter(msg => {
        if (msg.persistent) return false
        if (!msg.timestamp) return false
        const age = now - msg.timestamp.getTime()
        return age > 6000 // 6 seconds (5s display + 1s buffer)
      })

      if (expiredToasts.length > 0) {
        setMessages(prev =>
          prev
            .map(msg => {
              const isExpired = expiredToasts.some(
                expired => expired.id === msg.id,
              )
              if (isExpired) {
                // If message was sent to 'both', convert it to 'bell' only
                // If message was 'toast' only, remove it completely
                if (msg.target === 'both') {
                  return { ...msg, target: 'bell' as const }
                }
                // For 'toast' only messages, they will be filtered out below
                return null
              }
              return msg
            })
            .filter((msg): msg is Message => msg !== null),
        )
      }
    }, 1000) // Check every second

    return () => clearInterval(interval)
  }, [toastMessages])

  // Convenience helper functions for common toast notifications
  const showSuccess = useCallback(
    (message: string): string => {
      return addMessage({
        message,
        type: 'success',
        target: 'toast',
      })
    },
    [addMessage],
  )

  const showError = useCallback(
    (message: string): string => {
      return addMessage({
        message,
        type: 'error',
        target: 'toast',
      })
    },
    [addMessage],
  )

  const showInfo = useCallback(
    (message: string): string => {
      return addMessage({
        message,
        type: 'info',
        target: 'toast',
      })
    },
    [addMessage],
  )

  const showWarning = useCallback(
    (message: string): string => {
      return addMessage({
        message,
        type: 'warning',
        target: 'toast',
      })
    },
    [addMessage],
  )

  const contextValue: MessagingContextValue = {
    messages,
    toastMessages,
    bellMessages,
    unreadCount,
    persistenceWarning,
    addMessage,
    updateMessage,
    markAsRead,
    markAllAsRead,
    removeMessage,
    removeToastMessage,
    clearAllMessages,
    clearBellMessages,
    clearToastMessages,
    noteDismissedSourceIds,
    reconcileDismissedSourceIds,
    showSuccess,
    showError,
    showInfo,
    showWarning,
  }

  return (
    <MessagingContext.Provider value={contextValue}>
      {children}
    </MessagingContext.Provider>
  )
}

/**
 * Hook to access messaging functionality
 * @returns MessagingContextValue with all messaging functions and state
 * @throws Error if used outside MessagingProvider
 */
export const useMessaging = (): MessagingContextValue => {
  const context = useContext(MessagingContext)
  if (context === undefined) {
    throw new Error('useMessaging must be used within a MessagingProvider')
  }
  return context
}

export default MessagingContext
